import { GoonAvatar } from '@/components/goon-avatar';
import { InviteFriendsModal } from '@/components/modals/invite-friends-modal';
import { SeasonStatsModal } from '@/components/modals/season-stats-modal';
import { ClipboardIconButton, ClipboardLabel, ClipboardRoot } from '@/components/ui/clipboard';
import { useBadges } from '@/hooks/useBadges';
import { useCheckSocialConnectionStatus } from '@/hooks/useSocialConnections';
import { useWalletAddressUserData } from '@/hooks/useWalletAddressUserData';
import { truncateAddress } from '@/utils';
import { Button, Card, Heading, HStack, Icon, SimpleGrid, Stack, VStack } from '@chakra-ui/react';
import { HiMiniArrowLeft } from 'react-icons/hi2';
import { useNavigate } from 'react-router';
import { BadgesCard } from './BadgesCard';
import { SkySocietyCard } from './SkySocietyCard';
import { useProfileWalletAddress } from './useProfileWalletAddress';

const ProfileSummary = ({
  isCurrentUserAddress,
  profileAddress,
  rank,
}: {
  isCurrentUserAddress: boolean;
  profileAddress: string;
  rank: number;
}) => (
  <Stack direction={isCurrentUserAddress ? 'row' : 'column'} alignItems="center" gap={3}>
    <GoonAvatar seed={profileAddress.toLowerCase()} boxSize={isCurrentUserAddress ? 16 : 20} />
    <VStack alignItems={isCurrentUserAddress ? 'flex-start' : 'center'} gap={0}>
      <ClipboardRoot value={profileAddress} display="flex" alignItems="center">
        <ClipboardLabel mb={0}>
          <Heading fontFamily="Lufga, sans-serif" fontWeight={'700'} fontSize={'2xl'} lineHeight={1.4}>
            {truncateAddress(profileAddress)}
          </Heading>
        </ClipboardLabel>
        <ClipboardIconButton size="md" color="fg.muted" />
      </ClipboardRoot>
      <HStack fontFamily="Lufga, sans-serif" fontSize={'sm'}>
        <Heading size="lg" fontWeight="500">
          #{rank || 'N/A'}
        </Heading>
        <Heading size="lg" fontWeight="500" color="fg.muted">
          Global Rank
        </Heading>
      </HStack>
    </VStack>
  </Stack>
);

export const ProfileHeader = () => {
  const { profileAddress, isCurrentUserAddress } = useProfileWalletAddress();
  const { stats } = useWalletAddressUserData(profileAddress);
  const { badges } = useBadges(profileAddress);
  const { miles, stamps, referrals } = useSeasonOneStats();

  // Check for successful connection
  useCheckSocialConnectionStatus();

  if (!stats || !profileAddress) return null;

  return (
    <>
      {isCurrentUserAddress ? null : <ProfileBackButton />}
      <Card.Root
        bgColor={isCurrentUserAddress ? 'bg.subtle' : 'bg.accent3'}
        borderColor="border.subtle"
        px={isCurrentUserAddress ? 0 : 6}
        borderRadius={'3xl'}
        minW="fit-content"
      >
        <Card.Header p={0} pt={6}>
          <Stack
            justifyContent={isCurrentUserAddress ? 'space-between' : 'space-evenly'}
            direction={['column', 'column', 'row']}
            w="full"
          >
            <ProfileSummary
              isCurrentUserAddress={isCurrentUserAddress}
              profileAddress={profileAddress}
              rank={stats.xpRank}
            />
            {isCurrentUserAddress && (
              <HStack>
                {miles && stamps && referrals ? <SeasonStatsModal h={12} bgColor="bg" fontSize={'sm'} /> : null}
                <NavButton h={12} href={AppRoute.Leaderboard}>
                  Leaderboard
                </NavButton>
                <InviteFriendsModal h={12} fontSize={'sm'} />
              </HStack>
            )}
          </Stack>
        </Card.Header>
        <Card.Body spaceY={4} px={0}>
          <SimpleGrid h="full" w="full" gap={4} columns={[1, 1, 2]}>
            <SkySocietyCard isCurrentUserAddress={isCurrentUserAddress} profileAddress={profileAddress} />
            {badges && <BadgesCard badges={badges} />}
          </SimpleGrid>
        </Card.Body>
      </Card.Root>
    </>
  );
};

import { NavButton } from '@/components/nav-button';
import { AppRoute } from '@/constants/shared';
import { useSeasonOneStats } from '@/hooks/useSeasonOneStats';

export const ProfileBackButton = () => {
  const navigate = useNavigate();
  return (
    <Button
      onClick={() => navigate(-1)}
      direction="bk"
      h={8}
      w={12}
      bgColor="bg"
      mb={4}
      borderRadius={'3xl'}
      px={3}
      gap={1}
      variant="outline"
      bg="bg"
      _hover={{ bg: 'bg.muted' }}
    >
      <Icon boxSize={4}>
        <HiMiniArrowLeft />
      </Icon>
    </Button>
  );
};
